import styled from "styled-components";
import { firestore } from "../../App";
import corzoneso from "../../res/image/corzoneso.jpg";
import AmbientImage from "../Ambient";
import { useEffect, useRef, useState } from "react";
import {
	collection,
	doc,
	getDoc,
	getDocs,
	limit,
	orderBy,
	query,
	Timestamp,
	where,
} from "firebase/firestore";
import Loader from "../components/Loader";
import { ElevatedCard } from "../components/Components";
import { Link, useNavigate } from "react-router-dom";
import dateFormat from "dateformat";
import InternalNotificationComponent from "../components/dialog/InternalNotification";

const Container = styled.div``;

const LandingImage = styled(AmbientImage)`
	width: 100%;
	height: 372px;
	object-fit: cover;
	border-radius: 24px;

	@media (max-width: 480px) {
		height: 248px;
	}
`;

const Dashboard = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	justify-content: center;
	gap: 8px;
	margin: 48px 0;
`;

const DashboardCard = styled(ElevatedCard)`
	display: flex;
	flex-direction: column;
	width: 300px;
	height: 112px;
	gap: 12px;
`;

const DashboardCardTitle = styled.h3`
	margin-bottom: 6px;
`;

const DashboardCardBody = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	justify-content: center;
	align-items: center;
`;

const DashboardCardError = styled.div`
	display: flex;
	color: var(--color-error);
	gap: 12px;
	align-items: center;
`;

const DashboardCardInfo = styled.div`
	display: flex;
	color: var(--color-info);
	gap: 12px;
	align-items: center;
`;

const MeteoContent = styled.div`
	width: 100%;
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 12px;

	.status {
		display: flex;
		flex: 1 1 auto;
		justify-content: center;
		height: 48px;

		span {
			width: 48px;
			height: 48px;
			font-size: 48px;
			line-height: 48px;
		}
	}

	.content {
		display: flex;
		flex-direction: column;
		flex: 1 1 auto;
		gap: 6px;

		p {
			color: var(--color-on-surface-variant);
		}
	}
`;

const BookingContent = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 12px;

	.color {
		width: 18px;
		height: 18px;
		border: 1px solid var(--color-outline);
		border-radius: 50%;
		background: ${(props) => props.color};
	}

	.content {
		display: flex;
		flex-direction: column;
		gap: 6px;

		p {
			color: var(--color-on-surface-variant);
		}
	}
`;

const NoteContent = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 6px;

	span {
		color: var(--color-on-surface-variant);
	}

	p {
		overflow: hidden;
		text-overflow: ellipsis;
		display: -webkit-box;
		-webkit-line-clamp: 1;
		-webkit-box-orient: vertical;
	}
`;

const weatherDescDictionary = {
	"clear sky": "Cielo sereno",
	"few clouds": "Poche nuvole",
	"scattered clouds": "Nuvole sparse",
	"broken clouds": "Nuvole spezzate",
	"shower rain": "Pioggia a doccia",
	rain: "Pioggia",
	thunderstorm: "Temporale",
	snow: "Neve",
	mist: "Nebbia",
};

const weatherIconDictionary = {
	"01d": "clear_day",
	"01n": "bedtime",
	"02d": "partly_cloudy_day",
	"02n": "partly_cloudy_night",
	"03d": "cloud",
	"03n": "cloud",
	"04d": "cloud",
	"04n": "cloud",
	"09d": "rainy_light",
	"09n": "rainy_light",
	"10d": "rainy",
	"10n": "rainy",
	"11d": "thunderstorm",
	"11n": "thunderstorm",
	"13d": "ac_unit",
	"13n": "ac_unit",
	"50d": "foggy",
	"50n": "foggy",
};

const WindowBody = styled.section`
	max-width: 1200px;
	padding: 0 48px;
	margin: 0 auto;

	@media screen and (max-width: 768px) {
		padding: 0 24px;
	}

	@media screen and (max-width: 400px) {
		padding: 0 0;
	}

	h2 {
		text-align: center;
		color: var(--color-secondary);
		margin-bottom: 12px;
		font-weight: bold;
	}
`;

export default function HomePage() {
	const navigation = useNavigate();

	const [weatherData, setWeatherData] = useState(null);
	const [isYourBooking, setIsYourBooking] = useState(false);
	const [nextBooking, setNextBooking] = useState(null);
	const [lastNote, setLastNote] = useState(null);

	const ambientOptions = {
		insertCSS: true,
		retainAttributes: false,
		blur: 12,
	};

	/* Weather */
	const hasWeatherFetched = useRef(false);
	const [lat, lon] = [46.44640837986882, 8.934437917502919];
	useEffect(() => {
		async function fetchData() {
			if (hasWeatherFetched.current) return;
			hasWeatherFetched.current = true;

			fetch(
				`https://api.openweathermap.org/data/2.5/weather/?lat=${lat}&lon=${lon}&units=metric&APPID=${process.env.REACT_APP_OPENWEATHER_API_KEY}`
			)
				.then((res) => res.json())
				.then((result) => {
					const weatherData = {
						description:
							weatherDescDictionary[
								String(result.weather[0].main).toLowerCase()
							],
						icon: weatherIconDictionary[result.weather[0].icon],
						temp: result.main.temp,
					};

					setWeatherData(weatherData);
					console.log(result);
				})
				.catch((err) => {
					console.error(err);
					setWeatherData("error");
				});
		}

		fetchData();
	}, [lat, lon]);

	// firestore query
	const eventRef = collection(firestore, "events");
	const noteRef = collection(firestore, "notes");

	useEffect(() => {
		/* Load next event [START] */
		async function getNextBooking() {
			const eventQuery = query(
				eventRef,
				where(
					"timestampStartDate",
					">",
					Timestamp.fromDate(new Date())
				),
				orderBy("timestampStartDate", "asc"),
				limit(1)
			);
			const querySnapshot = await getDocs(eventQuery);

			querySnapshot.forEach((doc) => {
				console.log(doc.id, " => ", doc.data());
				const data = doc.data();

				if (data.uid) {
					getNextBookingUser(
						data.uid,
						`${data.startDate} - ${data.endDate}`
					);
				} else {
					setNextBooking("empty");
				}
			});
		}

		async function getNextBookingUser(uid, startEndDate) {
			const docRef = doc(firestore, "users", uid);
			const docSnap = await getDoc(docRef);

			if (docSnap.exists()) {
				let data = {
					name: docSnap.data().nome,
					color: docSnap.data().colore,
					startEndDate: startEndDate,
				};
				setNextBooking(data);
			} else {
				setNextBooking("error");
			}
		}
		/* Load next event [END] */

		/* Load last note [START] */
		async function getLastNote() {
			const noteQuery = query(
				noteRef,
				orderBy("timestamp", "desc"),
				limit(1)
			);
			const querySnapshot = await getDocs(noteQuery);

			querySnapshot.forEach((doc) => {
				const data = doc.data();

				if (data.text) {
					const noteData = {
						time: dateFormat(
							data.timestamp.toDate(),
							"dd.MM.yyyy - HH:mm"
						),
						note: data.text,
					};
					setLastNote(noteData);
				} else {
					setLastNote("empty");
				}
			});
		}
		/* Load last note [END] */

		getNextBooking();
		getLastNote();
	}, []);

	return (
		<Container>
			<LandingImage
				src={corzoneso}
				options={ambientOptions}
				alt="foto corzoneso"
			/>
			<Dashboard>
				{isYourBooking && (
					<DashboardCard title="Vedi prenotazione">
						<DashboardCardTitle>
							La tua prenotazione
						</DashboardCardTitle>
						<DashboardCardBody></DashboardCardBody>
					</DashboardCard>
				)}
				<DashboardCard>
					<DashboardCardTitle>Meteo adesso</DashboardCardTitle>
					<DashboardCardBody>
						{!weatherData ? (
							<Loader size={48} />
						) : weatherData === "error" ? (
							<DashboardCardError>
								<span className="material-symbol fill">
									error
								</span>
								<p>Impossibile caricare i dati</p>
							</DashboardCardError>
						) : weatherData === "empty" ? (
							<DashboardCardInfo>
								<span className="material-symbol fill">
									calendar_add_on
								</span>
								<p>Nessun meteo</p>
							</DashboardCardInfo>
						) : (
							<MeteoContent>
								<div className="status">
									<span className="material-symbol fill">
										{weatherData.icon}
									</span>
								</div>
								<div className="content">
									<p>{weatherData.temp}°C</p>
									<p>{weatherData.description}</p>
								</div>
							</MeteoContent>
						)}
					</DashboardCardBody>
				</DashboardCard>
				<DashboardCard
					title="Apri calendario"
					onClick={() => navigation("/app/calendario")}
				>
					<DashboardCardTitle>
						Prossima prenotazione
					</DashboardCardTitle>
					<DashboardCardBody>
						{!nextBooking ? (
							<Loader size={48} />
						) : nextBooking === "error" ? (
							<DashboardCardError>
								<span className="material-symbol fill">
									error
								</span>
								<p>Impossibile caricare i dati</p>
							</DashboardCardError>
						) : nextBooking === "empty" ? (
							<DashboardCardInfo>
								<span className="material-symbol fill">
									calendar_add_on
								</span>
								<p>Nessuna prenotazione</p>
							</DashboardCardInfo>
						) : (
							<BookingContent color={nextBooking.color}>
								<div className="color"></div>
								<div className="content">
									<div className="title-medium">
										{nextBooking.name}
									</div>
									<p>{nextBooking.startEndDate}</p>
								</div>
							</BookingContent>
						)}
					</DashboardCardBody>
				</DashboardCard>
				<DashboardCard>
					<DashboardCardTitle>Ultima nota</DashboardCardTitle>
					<DashboardCardBody>
						{!lastNote ? (
							<Loader size={48} />
						) : lastNote === "error" ? (
							<DashboardCardError>
								<span className="material-symbol fill">
									error
								</span>
								<p>Impossibile caricare i dati</p>
							</DashboardCardError>
						) : lastNote === "empty" ? (
							<DashboardCardInfo>
								<span className="material-symbol fill">
									calendar_add_on
								</span>
								<p>Nessuna nota</p>
							</DashboardCardInfo>
						) : (
							<NoteContent>
								<span className="body-small">
									{lastNote.time}
								</span>
								<p>{lastNote.note}</p>
							</NoteContent>
						)}
					</DashboardCardBody>
				</DashboardCard>
			</Dashboard>
			<WindowBody>
				<h2>Ciao</h2>
				<p>
					Abbiamo creato questo sito internet per gestire meglio la
					casa di Corzoneso. Finora i nonni si sono occupati di tutto,
					pagando le spese e mantenendo la casa in buone condizioni.
					Ora tocca a noi fare la nostra parte. Sappiamo che ci sono
					costi e lavori da fare, ma se collaboriamo possiamo
					continuare a goderci la casa. Ogni anno dobbiamo pagare
					circa 3'000fr per le spese fisse, come l'ipoteca,
					l'elettricità, la tassa sul turismo, ecc. Poi ci sono le
					spese extra. Queste cifre non includono il pellet e la legna
					da ardere.
				</p>
				<p>
					Per coprire le spese fisse abbiamo deciso di chiedere una
					tassa di soggiorno di fr.50.- a notte per la casa, più il
					consumo di elettricità. Siamo consapevoli che non è facile
					affittare la casa per più di 50 notti all'anno, quindi
					potremmo doverla affittare anche a persone fidate fuori
					dalla famiglia. Se affittiamo a esterni dobbiamo essere
					ancora più attenti a lasciare la casa pulita e ordinata. Per
					questo abbiamo preparato una checklist di pulizia. Per la
					biancheria da letto ognuno deve portare (obbligatoriamente)
					da casa un coprimaterasso, un copripiumino ed un
					copricuscino. Non usate i letti senza questi accessori!
				</p>
				<p>
					Vi chiediamo di rispettare la casa e di non lasciare le
					vostre cose dopo il soggiorno. (La nonna ha uno spazio per
					le sue cose). Se usate qualcosa che si esaurisce, provate a
					rimpiazzarlo o scrivete nella sezione{" "}
					<Link to="/app/gestione#note">Note</Link> cosa manca.
				</p>
				<p>
					Grazie per la vostra collaborazione, speriamo che vi
					troviate bene nella casa di Corzoneso.
				</p>
			</WindowBody>
		</Container>
	);
}
